package br.com.texeratx.boardgametimeapp.ui.screens.matches

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Badge
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.i18n.Strings
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppLoadingOverlay
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.screens.matches.detail.MatchDetailScreen
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDate
import br.com.texeratx.boardgametimeapp.ui.utils.uiToApiDate
import cafe.adriel.voyager.navigator.currentOrThrow

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MatchesUI(
    state: MatchesUiState,
    onSearch: (String) -> Unit,
    onApplyFilters: (MatchesFilter) -> Unit,
    onSortChange: (MatchSortOption) -> Unit,
    onBack: () -> Unit,
    onDismissError: () -> Unit
) {
    val S = LocalStrings.current
    val navigator = cafe.adriel.voyager.navigator.LocalNavigator.currentOrThrow
    var showFilters by remember { mutableStateOf(false) }

    AppScreenScaffold(
        topBar = {
            MatchesTopBar(
                count = state.matches.size,
                selected = state.sort,
                currentQuery = state.searchQuery,
                onSearch = onSearch,
                onSortChange = onSortChange,
                onOpenFilters = { showFilters = true },
                onBack = onBack
            )
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            Column(modifier = Modifier.fillMaxSize()) {
                if (state.filters.hasActiveFilters()) {
                    ActiveMatchesFiltersChips(
                        filters = state.filters,
                        availableGames = state.availableGames,
                        availablePlayers = state.availablePlayers,
                        onRemoveGame = { gameId ->
                            onApplyFilters(state.filters.copy(selectedGameIds = state.filters.selectedGameIds - gameId))
                        },
                        onRemovePlayer = { playerId ->
                            onApplyFilters(state.filters.copy(selectedPlayerIds = state.filters.selectedPlayerIds - playerId))
                        },
                        onRemoveDuration = {
                            onApplyFilters(state.filters.copy(minDuration = null, maxDuration = null))
                        },
                        onRemoveDate = {
                            onApplyFilters(state.filters.copy(startDateApi = null, endDateApi = null))
                        }
                    )
                }

                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(state.matches) { match ->
                        MatchItem(
                            match,
                            onClick = {
                                navigator.push(
                                    MatchDetailScreen(match)
                                )
                            }
                        )
                    }
                }
            }

            AppLoadingOverlay(isVisible = state.isLoading)

            state.error?.let {
                AlertDialog(
                    onDismissRequest = onDismissError,
                    confirmButton = {
                        TextButton(onClick = onDismissError) {
                            Text(S.ok)
                        }
                    },
                    title = { Text(S.errorTitle) },
                    text = { Text(it) }
                )
            }
        }

        if (showFilters) {
            MatchesFilterBottomSheet(
                current = state.filters,
                availableGames = state.availableGames,
                availablePlayers = state.availablePlayers,
                onApply = {
                    onApplyFilters(it)
                    showFilters = false
                },
                onDismiss = { showFilters = false }
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun MatchesTopBar(
    count: Int,
    selected: MatchSortOption,
    currentQuery: String,
    onSearch: (String) -> Unit,
    onSortChange: (MatchSortOption) -> Unit,
    onOpenFilters: () -> Unit,
    onBack: () -> Unit,
) {
    val S = LocalStrings.current
    var expanded by remember { mutableStateOf(false) }
    var isSearching by remember { mutableStateOf(currentQuery.isNotBlank()) }

    TopAppBar(
        title = {
            if (isSearching) {
                TextField(
                    value = currentQuery,
                    onValueChange = onSearch,
                    placeholder = { Text(S.searchMatch) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = TextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        focusedIndicatorColor = Color.Transparent,
                        unfocusedIndicatorColor = Color.Transparent
                    )
                )
            } else {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = S.matchesTitle,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )

                    Spacer(Modifier.width(8.dp))

                    Badge(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ) {
                        Text(count.toString())
                    }
                }
            }
        },
        navigationIcon = {
            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = S.back,
                modifier = Modifier
                    .padding(start = 12.dp)
                    .clickable { onBack() }
            )
        },
        colors = AppTopBarColors(),
        actions = {
            Icon(
                imageVector = if (isSearching) Icons.Default.Close else Icons.Default.Search,
                contentDescription = S.searchMatch,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable {
                        isSearching = !isSearching
                        if (!isSearching) onSearch("")
                    }
            )

            Icon(
                imageVector = Icons.Default.FilterList,
                contentDescription = S.matchFiltersTitle,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable { onOpenFilters() }
            )

            Row(
                modifier = Modifier
                    .padding(end = 12.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .clickable { expanded = true }
                    .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.15f))
                    .padding(horizontal = 10.dp, vertical = 6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    selected.label(S),
                    fontWeight = FontWeight.SemiBold
                )

                Spacer(Modifier.width(4.dp))

                Icon(
                    imageVector = Icons.Default.ArrowDropDown,
                    contentDescription = S.matchSortDateDesc
                )
            }

            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false }
            ) {
                DropdownMenuItem(
                    text = { Text(S.matchSortDateDesc) },
                    onClick = {
                        expanded = false
                        onSortChange(MatchSortOption.DATE_DESC)
                    }
                )

                DropdownMenuItem(
                    text = { Text(S.matchSortDateAsc) },
                    onClick = {
                        expanded = false
                        onSortChange(MatchSortOption.DATE_ASC)
                    }
                )

                DropdownMenuItem(
                    text = { Text(S.matchSortDurationDesc) },
                    onClick = {
                        expanded = false
                        onSortChange(MatchSortOption.DURATION_DESC)
                    }
                )

                DropdownMenuItem(
                    text = { Text(S.matchSortDurationAsc) },
                    onClick = {
                        expanded = false
                        onSortChange(MatchSortOption.DURATION_ASC)
                    }
                )
            }
        }
    )
}

@Composable
private fun ActiveMatchesFiltersChips(
    filters: MatchesFilter,
    availableGames: List<MatchFilterGameOption>,
    availablePlayers: List<MatchFilterPlayerOption>,
    onRemoveGame: (Long) -> Unit,
    onRemovePlayer: (Long) -> Unit,
    onRemoveDuration: () -> Unit,
    onRemoveDate: () -> Unit
) {
    val S = LocalStrings.current

    val gameNameById = availableGames.associate { it.id to it.name }
    val playerNameById = availablePlayers.associate { it.id to it.name }

    val chips = buildList {
        filters.selectedGameIds.forEach { gameId ->
            val label = S.matchFilterGameFormat(gameNameById[gameId] ?: gameId.toString())
            add(label to { onRemoveGame(gameId) })
        }

        filters.selectedPlayerIds.forEach { playerId ->
            val label = S.matchFilterPlayerFormat(playerNameById[playerId] ?: playerId.toString())
            add(label to { onRemovePlayer(playerId) })
        }

        if (filters.minDuration != null || filters.maxDuration != null) {
            val label = S.matchFilterDurationRangeFormat(
                filters.minDuration?.toString() ?: "-",
                filters.maxDuration?.toString() ?: "-"
            )
            add(label to onRemoveDuration)
        }

        if (!filters.startDateApi.isNullOrBlank() || !filters.endDateApi.isNullOrBlank()) {
            val label = S.matchFilterDateRangeFormat(
                filters.startDateApi?.let { apiToUiDate(it) }?.takeUnless { it == "-" } ?: "-",
                filters.endDateApi?.let { apiToUiDate(it) }?.takeUnless { it == "-" } ?: "-"
            )
            add(label to onRemoveDate)
        }
    }

    LazyRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        items(chips) { (label, onRemove) ->
            MatchFilterChip(label = label, onRemove = onRemove)
        }
    }
}

@Composable
private fun MatchFilterChip(
    label: String,
    onRemove: () -> Unit
) {
    val S = LocalStrings.current

    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.secondaryContainer)
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium
        )

        Spacer(Modifier.width(6.dp))

        Icon(
            imageVector = Icons.Default.Close,
            contentDescription = S.remove,
            modifier = Modifier
                .size(16.dp)
                .clickable { onRemove() }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun MatchesFilterBottomSheet(
    current: MatchesFilter,
    availableGames: List<MatchFilterGameOption>,
    availablePlayers: List<MatchFilterPlayerOption>,
    onApply: (MatchesFilter) -> Unit,
    onDismiss: () -> Unit
) {
    val S = LocalStrings.current

    var selectedGameIds by remember { mutableStateOf(current.selectedGameIds) }
    var selectedPlayerIds by remember { mutableStateOf(current.selectedPlayerIds) }
    var minDurationInput by remember { mutableStateOf(current.minDuration?.toString().orEmpty()) }
    var maxDurationInput by remember { mutableStateOf(current.maxDuration?.toString().orEmpty()) }
    var startDateInput by remember {
        mutableStateOf(current.startDateApi?.let { apiToUiDate(it) }?.takeUnless { it == "-" }.orEmpty())
    }
    var endDateInput by remember {
        mutableStateOf(current.endDateApi?.let { apiToUiDate(it) }?.takeUnless { it == "-" }.orEmpty())
    }

    val minDuration = minDurationInput.toIntOrNull()
    val maxDuration = maxDurationInput.toIntOrNull()
    val startDateApi = uiToApiDate(startDateInput)
    val endDateApi = uiToApiDate(endDateInput)

    val durationRangeInvalid = minDuration != null && maxDuration != null && minDuration > maxDuration
    val dateRangeInvalid = startDateApi != null && endDateApi != null && startDateApi > endDateApi

    ModalBottomSheet(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .verticalScroll(rememberScrollState())
        ) {
            Text(S.matchFiltersTitle, style = MaterialTheme.typography.titleLarge)
            Spacer(Modifier.height(16.dp))

            Text(S.matchFilterGames, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Spacer(Modifier.height(8.dp))
            availableGames.forEach { option ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable {
                            selectedGameIds = if (option.id in selectedGameIds) {
                                selectedGameIds - option.id
                            } else {
                                selectedGameIds + option.id
                            }
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Checkbox(
                        checked = option.id in selectedGameIds,
                        onCheckedChange = { checked ->
                            selectedGameIds = if (checked) selectedGameIds + option.id else selectedGameIds - option.id
                        }
                    )
                    Text(option.name)
                }
            }

            Spacer(Modifier.height(12.dp))
            Text(S.matchFilterPlayers, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Spacer(Modifier.height(8.dp))
            availablePlayers.forEach { option ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable {
                            selectedPlayerIds = if (option.id in selectedPlayerIds) {
                                selectedPlayerIds - option.id
                            } else {
                                selectedPlayerIds + option.id
                            }
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Checkbox(
                        checked = option.id in selectedPlayerIds,
                        onCheckedChange = { checked ->
                            selectedPlayerIds = if (checked) selectedPlayerIds + option.id else selectedPlayerIds - option.id
                        }
                    )
                    Text(option.name)
                }
            }

            Spacer(Modifier.height(12.dp))
            Text(S.matchFilterDuration, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Spacer(Modifier.height(8.dp))

            TextField(
                value = minDurationInput,
                onValueChange = { minDurationInput = it.filter(Char::isDigit) },
                label = { Text(S.matchFilterMinDuration) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(8.dp))

            TextField(
                value = maxDurationInput,
                onValueChange = { maxDurationInput = it.filter(Char::isDigit) },
                label = { Text(S.matchFilterMaxDuration) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(12.dp))
            Text(S.matchFilterDate, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Spacer(Modifier.height(8.dp))

            TextField(
                value = startDateInput,
                onValueChange = { startDateInput = it },
                label = { Text(S.matchFilterStartDate) },
                placeholder = { Text(S.matchDatePlaceholder) },
                modifier = Modifier.fillMaxWidth()
            )

            Spacer(Modifier.height(8.dp))

            TextField(
                value = endDateInput,
                onValueChange = { endDateInput = it },
                label = { Text(S.matchFilterEndDate) },
                placeholder = { Text(S.matchDatePlaceholder) },
                modifier = Modifier.fillMaxWidth()
            )

            if (durationRangeInvalid) {
                Spacer(Modifier.height(8.dp))
                Text(
                    text = S.matchFilterDurationRangeInvalid,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall
                )
            }

            if (dateRangeInvalid) {
                Spacer(Modifier.height(8.dp))
                Text(
                    text = S.matchFilterDateRangeInvalid,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall
                )
            }

            Spacer(Modifier.height(24.dp))

            Button(
                modifier = Modifier.fillMaxWidth(),
                enabled = !durationRangeInvalid && !dateRangeInvalid,
                onClick = {
                    onApply(
                        MatchesFilter(
                            selectedGameIds = selectedGameIds,
                            selectedPlayerIds = selectedPlayerIds,
                            minDuration = minDuration,
                            maxDuration = maxDuration,
                            startDateApi = startDateApi,
                            endDateApi = endDateApi
                        )
                    )
                }
            ) {
                Text(S.applyFilters)
            }

            Spacer(Modifier.height(12.dp))

            TextButton(
                modifier = Modifier.fillMaxWidth(),
                onClick = { onApply(MatchesFilter()) }
            ) {
                Text(S.clearFilters)
            }
        }
    }
}

private fun MatchSortOption.label(strings: Strings): String {
    return when (this) {
        MatchSortOption.DATE_DESC -> strings.matchSortDateDesc
        MatchSortOption.DATE_ASC -> strings.matchSortDateAsc
        MatchSortOption.DURATION_DESC -> strings.matchSortDurationDesc
        MatchSortOption.DURATION_ASC -> strings.matchSortDurationAsc
    }
}
